// EP-160 T160-104w: Settings -> คำขอข้อมูลส่วนบุคคล (owner and admin).
// docs/ep-160-support-access-and-privacy-in-the-app.md §2.7 and S6 (owner
// side). The tenant files one of six requests in the tenant app; the owner or
// an admin approves or rejects it here. Approving an erasure cannot be undone
// (money records stay, without the name), so it confirms first; rejecting
// always needs a reason, sent as `decision_note`. EP-162 item 9.2: an approved
// restriction, objection or withdrawal can be lifted, with a written reason.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { Alert, Badge, Button, Card, ConfirmDialog, DataTable, Modal, Pagination, Textarea } from '@/components/ui'
import type { BadgeProps, Column } from '@/components/ui'
import {
  isLiftable,
  useDataSubjectRequests,
  useLiftDataSubjectRequest,
  useUpdateDataSubjectRequest,
  type DataSubjectRequest,
  type UpdateDataSubjectRequestInput,
} from '@/hooks/useDataSubjectRequests'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores/useNotificationStore'

const NOTE_MAX_LENGTH = 1000

const STATUS_VARIANT: Record<DataSubjectRequest['status'], BadgeProps['variant']> = {
  pending: 'warning',
  processing: 'info',
  completed: 'success',
  rejected: 'error',
}

const isOpen = (r: DataSubjectRequest) => r.status === 'pending' || r.status === 'processing'

export function DataSubjectRequestsCard() {
  const { t } = useTranslation('settings')
  const formatDate = useDateFormatter()
  const translateError = useApiErrorTranslator('settings')
  const addToast = useNotificationStore((s) => s.addToast)
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = useDataSubjectRequests(page)
  const update = useUpdateDataSubjectRequest()
  const lift = useLiftDataSubjectRequest()
  const [erasing, setErasing] = useState<DataSubjectRequest | null>(null)
  const [rejecting, setRejecting] = useState<DataSubjectRequest | null>(null)
  const [lifting, setLifting] = useState<DataSubjectRequest | null>(null)

  const nameOf = (r: DataSubjectRequest) => r.customer_name ?? t('data_requests.unknown_customer')

  const decide = (input: UpdateDataSubjectRequestInput, request: DataSubjectRequest) => {
    update.mutate(input, {
      onSuccess: () => {
        setErasing(null)
        setRejecting(null)
        const key =
          input.status === 'rejected'
            ? 'data_requests.rejected'
            : request.request_type === 'access'
              ? 'data_requests.approved_access'
              : 'data_requests.approved'
        addToast({ type: 'success', message: t(key) })
      },
      onError: (err) => {
        const code = isAxiosError<{ error?: { code?: string } }>(err) ? err.response?.data?.error?.code : undefined
        const message =
          code === 'request_already_decided' || code === 'invalid_status'
            ? t(`data_requests.errors.${code}`)
            : code === 'unprocessable_entity'
              ? t('data_requests.errors.note_too_long')
              : translateError(err)
        addToast({ type: 'error', message })
        if (code === 'request_already_decided') {
          setErasing(null)
          setRejecting(null)
        }
      },
    })
  }

  const liftRestriction = (request: DataSubjectRequest, reason: string) => {
    lift.mutate(
      { id: request.id, reason },
      {
        onSuccess: (lifted) => {
          setLifting(null)
          addToast({
            type: 'success',
            message: lifted.customer_restricted
              ? t('data_requests.lifted_still_restricted', { name: nameOf(request) })
              : t('data_requests.lifted'),
          })
        },
        onError: (err) => {
          const code = isAxiosError<{ error?: { code?: string } }>(err) ? err.response?.data?.error?.code : undefined
          const known = ['request_not_liftable', 'lift_reason_required', 'lift_reason_too_long']
          addToast({ type: 'error', message: code && known.includes(code) ? t(`data_requests.errors.${code}`) : translateError(err) })
          if (code === 'request_not_liftable') setLifting(null)
        },
      },
    )
  }

  const approve = (r: DataSubjectRequest) => {
    if (r.request_type === 'erasure') setErasing(r)
    else decide({ id: r.id, status: 'completed' }, r)
  }

  const statusLabel = (r: DataSubjectRequest) =>
    r.request_type === 'access' && r.status === 'completed' && r.export_ready
      ? t('data_requests.statuses.link_sent')
      : t(`data_requests.statuses.${r.status}`)

  const columns: Column<DataSubjectRequest>[] = [
    { key: 'customer', header: t('data_requests.columns.customer'), render: (r) => nameOf(r) },
    { key: 'type', header: t('data_requests.columns.type'), render: (r) => t(`data_requests.types.${r.request_type}`) },
    // Below lg the table sits beside the sidebar and runs out of room, so the
    // due date (the one that matters) stays and the filing date drops out.
    {
      key: 'requested_at',
      header: t('data_requests.columns.requested_at'),
      hideBelow: 'lg',
      className: 'whitespace-nowrap',
      render: (r) => formatDate(r.requested_at),
    },
    { key: 'due_at', header: t('data_requests.columns.due_at'), className: 'whitespace-nowrap', render: (r) => formatDate(r.due_at) },
    {
      key: 'status',
      header: t('data_requests.columns.status'),
      render: (r) => <Badge variant={STATUS_VARIANT[r.status]}>{statusLabel(r)}</Badge>,
    },
    {
      key: 'note',
      header: t('data_requests.columns.note'),
      className: 'min-w-32 max-w-56 whitespace-pre-line break-words',
      render: (r) => (
        <div className="space-y-1">
          {r.note && <p>{r.note}</p>}
          {r.decision_note && (
            <p className="text-base-content/60">{t('data_requests.decision_note', { note: r.decision_note })}</p>
          )}
          {r.lifted_at && (
            <p className="text-base-content/60">
              {t('data_requests.lifted_note', { date: formatDate(r.lifted_at), reason: r.lift_reason ?? '' })}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: t('data_requests.columns.actions'),
      className: 'whitespace-nowrap',
      render: (r) =>
        isOpen(r) ? (
          <div className="flex flex-col gap-2 xl:flex-row">
            <Button size="sm" variant="primary" onClick={() => approve(r)} disabled={update.isPending}
              data-testid={`dsr-approve-${r.id}`}>
              {t('data_requests.approve')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setRejecting(r)} disabled={update.isPending}
              data-testid={`dsr-reject-${r.id}`}>
              {t('data_requests.reject')}
            </Button>
          </div>
        ) : isLiftable(r) ? (
          <Button size="sm" variant="outline" onClick={() => setLifting(r)} disabled={lift.isPending}
            data-testid={`dsr-lift-${r.id}`}>
            {t('data_requests.lift')}
          </Button>
        ) : null,
    },
  ]

  return (
    <Card title={t('data_requests.title')} description={t('data_requests.description')}>
      {isError ? (
        <Alert variant="error">{t('data_requests.load_failed')}</Alert>
      ) : (
        <DataTable
          columns={columns}
          data={data?.data ?? []}
          keyExtractor={(r) => r.id}
          loading={isLoading}
          emptyMessage={t('data_requests.empty')}
          size="sm"
        />
      )}
      {data && data.meta.total_pages > 1 && (
        <Pagination currentPage={page} totalPages={data.meta.total_pages} onPageChange={setPage} />
      )}

      <ConfirmDialog
        isOpen={erasing !== null}
        onClose={() => setErasing(null)}
        onConfirm={() => erasing && decide({ id: erasing.id, status: 'completed' }, erasing)}
        title={t('data_requests.erasure_confirm_title')}
        message={erasing ? t('data_requests.erasure_confirm_body', { name: nameOf(erasing) }) : ''}
        confirmLabel={t('data_requests.erasure_confirm_yes')}
        variant="error"
        loading={update.isPending}
      />
      <ReasonModal
        key={`reject-${rejecting?.id ?? 'closed'}`}
        isOpen={rejecting !== null}
        idPrefix="dsr-reject"
        title={t('data_requests.reject_title')}
        body={rejecting ? t('data_requests.reject_body', { name: nameOf(rejecting) }) : ''}
        label={t('data_requests.reject_reason_label')}
        requiredMessage={t('data_requests.reject_reason_required')}
        confirmLabel={t('data_requests.reject_confirm')}
        confirmVariant="error"
        loading={update.isPending}
        onClose={() => setRejecting(null)}
        onConfirm={(reason) => rejecting && decide({ id: rejecting.id, status: 'rejected', decision_note: reason }, rejecting)}
      />
      <ReasonModal
        key={`lift-${lifting?.id ?? 'closed'}`}
        isOpen={lifting !== null}
        idPrefix="dsr-lift"
        title={t('data_requests.lift_title')}
        body={lifting ? t('data_requests.lift_body', { name: nameOf(lifting) }) : ''}
        label={t('data_requests.lift_reason_label')}
        requiredMessage={t('data_requests.lift_reason_required')}
        confirmLabel={t('data_requests.lift_confirm')}
        confirmVariant="primary"
        loading={lift.isPending}
        onClose={() => setLifting(null)}
        onConfirm={(reason) => lifting && liftRestriction(lifting, reason)}
      />
    </Card>
  )
}

interface ReasonModalProps {
  isOpen: boolean
  // Ids of the textarea (`${idPrefix}-reason`) and the confirm button
  // (`${idPrefix}-confirm`).
  idPrefix: string
  title: string
  body: string
  label: string
  requiredMessage: string
  confirmLabel: string
  confirmVariant: 'error' | 'primary'
  loading: boolean
  onClose: () => void
  onConfirm: (reason: string) => void
}

// A written reason for a decision: rejecting a request, lifting a restriction.
function ReasonModal({
  isOpen,
  idPrefix,
  title,
  body,
  label,
  requiredMessage,
  confirmLabel,
  confirmVariant,
  loading,
  onClose,
  onConfirm,
}: ReasonModalProps) {
  const { t: tc } = useTranslation('common')
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')

  const submit = () => {
    const trimmed = reason.trim()
    if (!trimmed) {
      setError(requiredMessage)
      return
    }
    onConfirm(trimmed)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {tc('actions.cancel')}
          </Button>
          <Button variant={confirmVariant} onClick={submit} loading={loading} data-testid={`${idPrefix}-confirm`}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="mb-3 text-base-content/70">{body}</p>
      <Textarea
        id={`${idPrefix}-reason`}
        label={label}
        required
        value={reason}
        maxLength={NOTE_MAX_LENGTH}
        onChange={(e) => {
          setReason(e.target.value)
          if (error) setError('')
        }}
        error={error}
        rows={3}
      />
    </Modal>
  )
}
